import { Box, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';

const toSlug = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

interface Heading {
  level: 2 | 3;
  text: string;
  id: string;
}

const extractHeadings = (markdown: string): Heading[] =>
  markdown
    .split('\n')
    .filter((line) => /^#{2,3} /.test(line))
    .map((line) => {
      const level = line.startsWith('### ') ? 3 : 2;
      const text = line.replace(/^#{2,3} /, '').replace(/[*_`]/g, '').trim();
      return { level, text, id: toSlug(text) };
    });

export const TableOfContents: React.FC<{ content: string }> = ({ content }) => {
  const headings = extractHeadings(content);
  const [activeId, setActiveId] = useState<string>('');

  useEffect(() => {
    if (headings.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: '-10% 0px -80% 0px' }
    );

    headings.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [content]);

  if (headings.length === 0) return null;

  return (
    <Box>
      <Typography
        sx={{
          color: 'rgba(255,255,255,0.4)',
          fontSize: '11px',
          letterSpacing: '0.1em',
          fontWeight: 700,
          textTransform: 'uppercase',
          display: 'block',
          mb: 2,
        }}>
        On this page
      </Typography>
      <Box
        component="nav"
        sx={{ borderLeft: '1px solid rgba(255,255,255,0.1)', pl: 2 }}>
        {headings.map(({ level, text, id }) => (
          <Box key={id} sx={{ mb: 1.5 }}>
            <a
              href={`#${id}`}
              onClick={(e) => {
                e.preventDefault();
                document
                  .getElementById(id)
                  ?.scrollIntoView({ behavior: 'smooth' });
              }}
              style={{ textDecoration: 'none' }}>
              <Typography
                variant="body2"
                sx={{
                  pl: level === 3 ? 1.5 : 0,
                  fontSize: level === 3 ? '12px' : '13px',
                  color:
                    activeId === id ? 'white' : 'rgba(255,255,255,0.4)',
                  fontWeight: activeId === id ? 600 : 400,
                  lineHeight: 1.5,
                  transition: 'color 0.2s ease',
                  '&:hover': { color: 'rgba(255,255,255,0.75)' },
                }}>
                {text}
              </Typography>
            </a>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
